import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FiMail, FiLock, FiUser, FiEye, FiEyeOff,
  FiBriefcase, FiCheck, FiX, FiAlertCircle, FiImage
} from 'react-icons/fi';
import { HiOutlineBuildingOffice2 } from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo/Logo';
import GoogleSignIn from '../../components/GoogleSignIn/GoogleSignIn';
import AvatarPickerModal from '../../components/AvatarPickerModal/AvatarPickerModalStatic';
import '../../components/AvatarPickerModal/AvatarPickerModalStatic.css';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-number-input';
import { authAPI } from '../../services/api';
import './Register.css';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarModalTab, setAvatarModalTab] = useState('pick'); // 'pick' o 'upload'
  const [avatar, setAvatar] = useState(null); // { options, dataUrl }
  const [touched, setTouched] = useState({});

  // Load reCAPTCHA v3 script
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector(`script[src*="recaptcha/api.js"]`);
      if (existingScript) existingScript.remove();
    };
  }, []);

  // Wait for reCAPTCHA to be ready
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.execute) {
        resolve();
      } else {
        const checkInterval = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.execute) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
        
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve();
        }, 5000);
      }
    });
  };
  
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    phone: '',
    institution: '',
    occupation: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  // Validaciones
  const validations = useMemo(() => {
    const username = formData.username.trim();
    const email = formData.email.trim();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    return {
      username: {
        isValid: username.length >= 3 && /^[a-zA-Z0-9_]+$/.test(username),
        message: username.length < 3 
          ? 'Mínimo 3 caracteres' 
          : !/^[a-zA-Z0-9_]+$/.test(username)
          ? 'Solo letras, números y guiones bajos'
          : ''
      },
      name: {
        isValid: formData.name.trim().length >= 2,
        message: 'Mínimo 2 caracteres'
      },
      email: {
        isValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
        message: 'Ingresa un correo válido'
      },
      phone: {
        isValid: !formData.phone || /^\+\d{7,15}$/.test(formData.phone),
        message: 'Ingresa un teléfono válido'
      },
      password: {
        length: password.length >= 8,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /[0-9]/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        isValid: password.length >= 8 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /[0-9]/.test(password)
      },
      confirmPassword: {
        isValid: confirmPassword === password && confirmPassword.length > 0,
        message: confirmPassword !== password ? 'Las contraseñas no coinciden' : ''
      },
      terms: {
        isValid: formData.acceptTerms,
        message: 'Debes aceptar los términos'
      }
    };
  }, [formData]);

  const isFormValid = useMemo(() => {
    return validations.username.isValid &&
           validations.name.isValid &&
           validations.email.isValid &&
           validations.phone.isValid &&
           validations.password.isValid &&
           validations.confirmPassword.isValid &&
           validations.terms.isValid;
  }, [validations]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      setLoading(true);
      const response = await authAPI.googleAuth(credential);
      
      if (response.data && response.data.user && response.data.token) {
        login(response.data.user, response.data.token);
        
        if (response.data.isNewUser) {
          toast.success('¡Cuenta creada exitosamente! Bienvenido a Graphyra');
        } else {
          toast.success('¡Bienvenido de nuevo!');
        }
        
        // Check if profile completion is needed
        if (response.data.needsCompletion) {
          navigate('/complete-profile');
        } else {
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Google register error:', error);
      toast.error(error.response?.data?.message || 'Error al registrarse con Google');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      toast.error('Por favor completa todos los campos correctamente');
      setTouched({
        username: true,
        name: true,
        email: true,
        password: true,
        confirmPassword: true,
        terms: true
      });
      return;
    }

    try {
      setLoading(true);

      // Execute reCAPTCHA v3
      await waitForRecaptcha();
      let recaptchaToken = '';
      if (RECAPTCHA_SITE_KEY && window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'register' });
        } catch (error) {
          console.error('reCAPTCHA error:', error);
        }
      }
      
      const payload = {
        username: formData.username.trim(),
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone || null,
        institution: formData.institution?.trim() || null,
        occupation: formData.occupation?.trim() || null,
        password: formData.password,
        avatar: avatar?.url || null,
        acceptTerms: formData.acceptTerms,
        recaptchaToken
      };

      const response = await authAPI.register(payload);
      
      console.log('📥 Registration response:', response);
      console.log('📥 Response data:', response.data);
      console.log('📥 emailSent field:', response.data?.emailSent);
      
      // New registration flow: user must verify email first
      if (response.data && response.data.emailSent) {
        toast.success('¡Registro exitoso! Por favor revisa tu correo para verificar tu cuenta.');
        
        // Show email verification message
        setFormData({
          username: '',
          name: '',
          email: '',
          phone: '',
          institution: '',
          occupation: '',
          password: '',
          confirmPassword: '',
          acceptTerms: false
        });
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Revisa tu correo para verificar tu cuenta antes de iniciar sesión',
              email: payload.email
            } 
          });
        }, 3000);
      } else {
        console.error('❌ Invalid response - missing emailSent field');
        console.error('❌ Full response structure:', JSON.stringify(response, null, 2));
        toast.error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.message || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const getInputClassName = (field, isValid) => {
    if (!touched[field]) return 'form-input';
    return `form-input ${isValid ? 'valid' : 'invalid'}`;
  };

  const renderValidationIcon = (field, isValid) => {
    if (!touched[field]) return null;
    return isValid ? 
      <FiCheck className="validation-icon valid" /> : 
      <FiX className="validation-icon invalid" />;
  };

  return (
    <div className="auth-page register-page">
      <div className="auth-container register-container">
        <div className="auth-card register-card">
          <div className="auth-header">
            <Logo size="large" showText={true} />
            <h1 className="auth-title">Crear Cuenta</h1>
            <p className="auth-subtitle">
              Únete a nuestra comunidad y comienza a explorar
            </p>
          </div>

          <GoogleSignIn onSuccess={handleGoogleSuccess} />
          
          <div className="auth-divider">
            <span>o</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form register-form">
            {/* Usuario */}
            <div className="form-group">
              <label className="form-label">
                Usuario <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiUser className="input-icon" />
                </span>
                <input
                  type="text"
                  name="username"
                  className={getInputClassName('username', validations.username.isValid)}
                  placeholder="tu_usuario"
                  value={formData.username}
                  onChange={handleChange}
                  onBlur={() => handleBlur('username')}
                  maxLength={20}
                />
                {renderValidationIcon('username', validations.username.isValid)}
              </div>
              {touched.username && !validations.username.isValid && (
                <span className="validation-message error">
                  <FiAlertCircle /> {validations.username.message}
                </span>
              )}
            </div>

            {/* Nombre Real */}
            <div className="form-group">
              <label className="form-label">
                Nombre Completo <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiUser className="input-icon" />
                </span>
                <input
                  type="text"
                  name="name"
                  className={getInputClassName('name', validations.name.isValid)}
                  placeholder="Tu nombre real"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                />
                {renderValidationIcon('name', validations.name.isValid)}
              </div>
              {touched.name && !validations.name.isValid && (
                <span className="validation-message error">
                  <FiAlertCircle /> {validations.name.message}
                </span>
              )}
            </div>

            {/* Correo */}
            <div className="form-group">
              <label className="form-label">
                Correo Electrónico <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiMail className="input-icon" />
                </span>
                <input
                  type="email"
                  name="email"
                  className={getInputClassName('email', validations.email.isValid)}
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur('email')}
                />
                {renderValidationIcon('email', validations.email.isValid)}
              </div>
              {touched.email && !validations.email.isValid && (
                <span className="validation-message error">
                  <FiAlertCircle /> {validations.email.message}
                </span>
              )}
            </div>

            {/* Teléfono (Opcional) */}
            <div className="form-group">
              <label className="form-label">
                Teléfono <span className="optional">(Opcional)</span>
              </label>
              <div className="input-wrapper phone-wrapper">
                <div className="phone-input-container">
                  <PhoneInput
                    international
                    defaultCountry="CO"
                    countryCallingCodeEditable={false}
                    value={formData.phone || undefined}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, phone: value || '' }));
                    }}
                    onBlur={() => handleBlur('phone')}
                    className={touched.phone ? `phone-input ${validations.phone.isValid ? 'valid' : 'invalid'}` : 'phone-input'}
                    placeholder="Ej: +57 300 123 4567"
                  />
                  {formData.phone && renderValidationIcon('phone', validations.phone.isValid)}
                </div>
              </div>
            </div>

            {/* Avatar/Foto */}
            <div className="form-group avatar-group">
              <label className="form-label">
                Foto o Avatar <span className="optional">(Opcional)</span>
              </label>

              <div className="avatar-inline">
                <div className="avatar-inline-preview">
                  {avatar?.url ? (
                    <img src={avatar.url} alt="Avatar seleccionado" />
                  ) : (
                    <div className="avatar-inline-placeholder">
                      <FiImage />
                    </div>
                  )}
                </div>
                <div className="avatar-buttons-group">
                  <button
                    type="button"
                    className="btn btn-secondary avatar-btn"
                    onClick={() => {
                      setAvatarModalTab('pick');
                      setAvatarModalOpen(true);
                    }}
                  >
                    Escoger Avatar
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary avatar-btn"
                    onClick={() => {
                      setAvatarModalTab('upload');
                      setAvatarModalOpen(true);
                    }}
                  >
                    Subir Foto
                  </button>
                </div>
              </div>

              <AvatarPickerModal
                isOpen={avatarModalOpen}
                onClose={() => setAvatarModalOpen(false)}
                value={avatar}
                onSelect={(next) => setAvatar(next)}
                initialTab={avatarModalTab}
              />
            </div>

            {/* Institución (Opcional) */}
            <div className="form-group">
              <label className="form-label">
                Institución u Organización <span className="optional">(Opcional)</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <HiOutlineBuildingOffice2 className="input-icon" />
                </span>
                <input
                  type="text"
                  name="institution"
                  className="form-input"
                  placeholder="Universidad, empresa, etc."
                  value={formData.institution}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Ocupación (Opcional) */}
            <div className="form-group">
              <label className="form-label">
                Ocupación <span className="optional">(Opcional)</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiBriefcase className="input-icon" />
                </span>
                <input
                  type="text"
                  name="occupation"
                  className="form-input"
                  placeholder="Estudiante, desarrollador, etc."
                  value={formData.occupation}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Contraseña */}
            <div className="form-group">
              <label className="form-label">
                Contraseña <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiLock className="input-icon" />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className={getInputClassName('password', validations.password.isValid)}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                />
                <button
                  type="button"
                  className="input-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              
              {/* Indicadores de contraseña */}
              <div className="password-requirements">
                <div className={`requirement ${validations.password.length ? 'met' : ''}`}>
                  {validations.password.length ? <FiCheck /> : <FiX />}
                  <span>Mínimo 8 caracteres</span>
                </div>
                <div className={`requirement ${validations.password.uppercase ? 'met' : ''}`}>
                  {validations.password.uppercase ? <FiCheck /> : <FiX />}
                  <span>Una mayúscula</span>
                </div>
                <div className={`requirement ${validations.password.lowercase ? 'met' : ''}`}>
                  {validations.password.lowercase ? <FiCheck /> : <FiX />}
                  <span>Una minúscula</span>
                </div>
                <div className={`requirement ${validations.password.number ? 'met' : ''}`}>
                  {validations.password.number ? <FiCheck /> : <FiX />}
                  <span>Un número</span>
                </div>
              </div>
            </div>

            {/* Confirmar Contraseña */}
            <div className="form-group">
              <label className="form-label">
                Confirmar Contraseña <span className="required">*</span>
              </label>
              <div className="input-wrapper">
                <span className="input-icon-wrapper">
                  <FiLock className="input-icon" />
                </span>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className={getInputClassName('confirmPassword', validations.confirmPassword.isValid)}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={() => handleBlur('confirmPassword')}
                />
                <button
                  type="button"
                  className="input-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
                {renderValidationIcon('confirmPassword', validations.confirmPassword.isValid)}
              </div>
              {touched.confirmPassword && !validations.confirmPassword.isValid && (
                <span className="validation-message error">
                  <FiAlertCircle /> {validations.confirmPassword.message}
                </span>
              )}
            </div>

            {/* Términos y Condiciones */}
            <div className="form-group terms-group">
              <label className={`checkbox-label ${touched.terms && !validations.terms.isValid ? 'error' : ''}`}>
                <input 
                  type="checkbox" 
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  onBlur={() => handleBlur('terms')}
                />
                <span className="checkbox-custom">
                  {formData.acceptTerms && <FiCheck />}
                </span>
                <span className="checkbox-text">
                  Acepto los{' '}
                  <Link to="/terms" className="link" target="_blank">
                    términos y condiciones
                  </Link>
                  {' '}y la{' '}
                  <Link to="/privacy" className="link" target="_blank">
                    política de privacidad
                  </Link>
                  <span className="required">*</span>
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className={`btn btn-primary btn-block btn-register ${isFormValid ? 'ready' : ''}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-small"></span>
                  Creando cuenta...
                </>
              ) : (
                'Crear Cuenta'
              )}
            </button>
          </form>

          <p className="auth-footer">
            ¿Ya tienes una cuenta?{' '}
            <Link to="/login" className="link-primary">
              Inicia sesión aquí
            </Link>
          </p>
        </div>

        <div className="auth-visual register-visual">
          <div className="visual-overlay"></div>
          <div className="visual-content">
            <h2>Comienza tu aventura</h2>
            <p>Descubre conocimiento, crea contenido y conecta con mentes curiosas</p>
            <div className="visual-features">
              <div className="visual-feature">
                <span>📚</span>
                <span>Aprende con la Academia</span>
              </div>
              <div className="visual-feature">
                <span>🎮</span>
                <span>Juegos educativos</span>
              </div>
              <div className="visual-feature">
                <span>📺</span>
                <span>Contenido exclusivo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
